import { Alert, Button, DatePicker, FilterChip, Input, Textarea, TimePicker } from '@indipia/ui-core';
import {
  LOAD_PHOTO_RULE,
  SharePhotoPicker,
  formatMonthDayDow,
  nowTimeKst,
  todayKst,
  type SharePhotoItem,
} from '@indipia/shared-features';
import {
  DRIVER_NAME_MAX,
  ISSUE_NOTE_MAX,
  MSG,
  PHOTO_MAX,
  isArrivalLate,
  type DeliveryFieldErrors,
  type DeliveryFormValues,
} from '../../pages/share/vendorDelivery.logic';

// C-4-1 배송정보 등록·수정 폼. 상태는 부모(VendorDeliveryPage)가 가짐.

interface Props {
  mode: 'create' | 'edit';
  values: DeliveryFormValues;
  errors: DeliveryFieldErrors;
  deliveryDate: string;
  formError: string | null;
  photoNotice: string | null;
  photoItems: SharePhotoItem[];
  photoDisabled: boolean;
  failedPhotoCount: number;
  blockLabel: string | null;
  submitting: boolean;
  onChange: (patch: Partial<DeliveryFormValues>) => void;
  onSubmit: () => void;
  onCancel?: () => void;
  onAddFiles: (files: File[]) => void;
  onRemovePhoto: (key: string) => void;
  onRetryPhoto: (key: string) => void;
  onOpenPhoto: (key: string) => void;
  onPhotoNotice: (text: string) => void;
}

export function DeliveryForm(p: Props) {
  const { values, errors } = p;
  const isToday = values.arrivalDate === todayKst();
  const late = isArrivalLate(values.arrivalDate, p.deliveryDate);

  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        p.onSubmit();
      }}
    >
      {p.formError && (
        <Alert kind="error" role="alert" aria-live="assertive">
          {p.formError}
        </Alert>
      )}
      {late && (
        <Alert kind="warning">
          {MSG.lateWarning(formatMonthDayDow(p.deliveryDate))}
        </Alert>
      )}

      <section className="flex flex-col gap-3">
        <DatePicker
          id="delivery-arrival-date"
          size="lg"
          label={MSG.labelArrivalDate}
          value={values.arrivalDate}
          error={errors.arrivalDate}
          onChange={(d) => p.onChange({ arrivalDate: d })}
        />
        <TimePicker
          id="delivery-arrival-time"
          size="lg"
          label={MSG.labelArrivalTime}
          value={values.arrivalTime}
          error={errors.arrivalTime}
          after={isToday ? nowTimeKst() : undefined}
          onChange={(t) => p.onChange({ arrivalTime: t })}
        />
      </section>

      <section className="flex flex-col gap-3">
        <Input
          id="delivery-driver-name"
          size="lg"
          label={MSG.labelDriverName}
          maxLength={DRIVER_NAME_MAX}
          value={values.driverName}
          error={errors.driverName}
          onChange={(e) => p.onChange({ driverName: e.target.value })}
        />
        <Input
          id="delivery-driver-phone"
          size="lg"
          label={MSG.labelDriverPhone}
          type="tel"
          inputMode="tel"
          autoComplete="off"
          placeholder={MSG.driverPhonePlaceholder}
          value={values.driverPhone}
          error={errors.driverPhone}
          onChange={(e) => p.onChange({ driverPhone: e.target.value })}
        />
      </section>

      <section className="flex flex-col gap-2" aria-busy={p.photoItems.some((i) => i.status === 'uploading')}>
        <div className="text-body-sm font-semibold text-text-primary">{MSG.photoSectionTitle(PHOTO_MAX)}</div>
        {p.photoNotice && (
          <Alert kind="warning" role="alert">
            <span className="whitespace-pre-line">{p.photoNotice}</span>
          </Alert>
        )}
        <SharePhotoPicker
          items={p.photoItems}
          rule={LOAD_PHOTO_RULE}
          accept=".jpg,.jpeg,.png,.webp,.heic,.heif,image/*"
          disabled={p.photoDisabled || p.submitting}
          addLabel={MSG.photoAdd}
          onAddFiles={p.onAddFiles}
          onRemove={p.onRemovePhoto}
          onRetry={p.onRetryPhoto}
          onOpen={p.onOpenPhoto}
          onNotice={p.onPhotoNotice}
        />
        {p.failedPhotoCount > 0 && (
          <p className="text-caption text-text-muted" aria-live="polite">
            {MSG.photoFailedCount(p.failedPhotoCount)}
          </p>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <div id="delivery-issue-label" className="text-body-sm font-semibold text-text-primary">
          {MSG.labelIssue}
        </div>
        <div role="group" aria-labelledby="delivery-issue-label" className="flex gap-2">
          <FilterChip
            size="lg"
            active={values.issueYn === 'N'}
            aria-pressed={values.issueYn === 'N'}
            onClick={() => p.onChange({ issueYn: 'N', issueNote: '' })}
          >
            {MSG.issueNone}
          </FilterChip>
          <FilterChip
            size="lg"
            active={values.issueYn === 'Y'}
            aria-pressed={values.issueYn === 'Y'}
            onClick={() => p.onChange({ issueYn: 'Y' })}
          >
            {MSG.issueYes}
          </FilterChip>
        </div>
        {errors.issueYn && <p className="text-caption text-error">{errors.issueYn}</p>}
        {values.issueYn === 'Y' && (
          <div aria-live="polite">
            <Textarea
              id="delivery-issue-note"
              label={MSG.labelIssueNote}
              maxLength={ISSUE_NOTE_MAX}
              placeholder={MSG.issueNotePlaceholder}
              value={values.issueNote}
              error={errors.issueNote}
              onChange={(e) => p.onChange({ issueNote: e.target.value })}
            />
            <p className="mt-1 text-right text-caption text-text-muted">
              {values.issueNote.length}/{ISSUE_NOTE_MAX}
            </p>
          </div>
        )}
      </section>

      <div className="flex flex-col gap-2">
        <Button type="submit" size="lg" className="w-full" loading={p.submitting} disabled={p.blockLabel !== null}>
          {p.blockLabel ?? (p.mode === 'edit' ? MSG.btnEdit : MSG.btnRegister)}
        </Button>
        {p.onCancel && (
          <Button type="button" variant="secondary" size="lg" className="w-full" disabled={p.submitting} onClick={p.onCancel}>
            {MSG.cancel}
          </Button>
        )}
      </div>
    </form>
  );
}
